import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Input,
  input,
  signal,
} from '@angular/core';
import { Task } from '@modules/sales/models/task.model';
import { TaskService } from '@modules/sales/services/task.service';
import { KaufmannSettingsService } from '@modules/settings/services/kfm-settings.service';
import { ArticleItem } from '@modules/shopping/models/article-item.model';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
  HsButtonModule,
  HsCardModule,
  HsDividerModule,
  HsFormFieldModule,
  HsIconModule,
  HsInputModule,
  HsMenuModule,
  HsSelectModule,
  HsSelectOption,
  HsTooltipModule,
} from '@hsetu/ngui-components';
import { HsLayerModule } from '@hsetu/ngui-resources';
import {
  ICON_DELETE,
  ICON_DRAG_DROP,
  ICON_EDIT,
  ICON_PLUS,
  ICON_SHOPPING_CART,
} from '@shared/ui/icons/icon-constants';
import { DomSanitizer } from '@angular/platform-browser';
import { ArticleItemService } from '@modules/shopping/services/article-item.service';
import { KfmAppItemType } from '@modules/shopping/enums/item-type.enum';
import { KfmAppItemApplyToType } from '@modules/shopping/enums/item-apply-to-type.enum';
import { Dialog } from '@angular/cdk/dialog';
import { KfmArticleDialogComponent } from '../article-dialog/kfm-article-dialog.component';
import { KfmTextArticleDialogComponent } from '../text-article-dialog/kfm-text-article-dialog.component';
import { KfmAppNewDiscountSurchargeItemDialogComponent } from '../discount-surcharge-article-dialog/kfm-discount-surcharge-article-dialog.component';
import { KfmArticleCatalogDialogComponent } from '../article-catalog-dialog/kfm-article-catalog-dialog.component';
import { KfmYesNoDialogComponent } from '@shared/ui/components/features/dialogs/yes-no-dialog/kfm-yes-no-dialog.component';
import {
  ADD_ITEM_LABEL,
  ARTICLE_CATALOG_LABEL,
  ARTICLE_NUMBER_LABEL,
  DELETE_ITEM_CONFIRMATION_LABEL,
  DELETE_ITEM_LABEL,
  DISCOUNT_LABEL,
  DRAG_AND_DROP_LABEL,
  EDIT_ITEM_LABEL,
  ITEM_LABEL,
  MANUFACTURER_LABEL,
  NET_EP_LABEL,
  NET_GP_LABEL,
  NEW_ITEM_LABEL,
  NEW_SURCHARGE_DISCOUNT_LABEL,
  NEW_TEXT_ITEM_LABEL,
  PLUS_MWST_LABEL,
  PRICE_LABEL,
  QUANTITY_LABEL,
  REFERENCE_VALUE_LABEL,
  SHOPPING_CART_LABEL,
  SHORT_TEXT_LABEL,
  SUM_LABEL,
  SURCHARGE_LABEL,
  UNIT_LABEL,
  VAT_SHORT_LABEL,
} from '@modules/shopping/i18n/label-text-constants';

@Component({
  selector: 'kfm-shopping-cart',
  imports: [
    CommonModule,
    HsCardModule,
    HsLayerModule,
    HsIconModule,
    HsTooltipModule,
    HsDividerModule,
    HsButtonModule,
    HsInputModule,
    HsFormFieldModule,
    HsSelectModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    HsMenuModule,
  ],
  templateUrl: './kfm-shopping-cart.component.html',
})
export class KfmShoppingCartComponent {
  private readonly _settings = inject(KaufmannSettingsService);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _itemService = inject(ArticleItemService);
  private readonly _taskService = inject(TaskService);
  private readonly _dialog = inject(Dialog);

  private isDialogOpen = false;

  private _owner: Task | null = null;
  private readonly _ownerSignal = signal<Task | null>(null);

  private readonly _items = signal<ArticleItem[]>([]);

  protected readonly dragAndDrop = signal<boolean>(false);

  protected readonly shoppingCartIcon =
    this._sanitizer.bypassSecurityTrustHtml(ICON_SHOPPING_CART);

  protected readonly iconNew =
    this._sanitizer.bypassSecurityTrustHtml(ICON_PLUS);

  protected readonly iconEdit =
    this._sanitizer.bypassSecurityTrustHtml(ICON_EDIT);

  protected readonly iconDelete =
    this._sanitizer.bypassSecurityTrustHtml(ICON_DELETE);

  protected readonly iconDragDrop =
    this._sanitizer.bypassSecurityTrustHtml(ICON_DRAG_DROP);

  protected readonly cartItems = computed(() => {
    const items = this._items();
    return items;
  });

  private readonly pendingOperations = signal(0);
  public readonly isSaving = computed(() => this.pendingOperations() > 0);

  private beginSaving(): void {
    this.pendingOperations.update((v) => v + 1);
  }

  private endSaving(): void {
    this.pendingOperations.update((v) => Math.max(0, v - 1));
  }

  // ── i18n ──────────────────────────────────────────────────────────────────
  protected readonly itemLabel = ITEM_LABEL;
  protected readonly shoppingCartLabel = SHOPPING_CART_LABEL;
  protected readonly newItemLabel = NEW_ITEM_LABEL;
  protected readonly newTextItemLabel = NEW_TEXT_ITEM_LABEL;
  protected readonly newSurchargeDiscountLabel = NEW_SURCHARGE_DISCOUNT_LABEL;
  protected readonly articleCatalogLabel = ARTICLE_CATALOG_LABEL;
  protected readonly referenceValueLabel = REFERENCE_VALUE_LABEL;
  protected readonly priceLabel = PRICE_LABEL;
  protected readonly discountLabel = DISCOUNT_LABEL;
  protected readonly surchargeLabel = SURCHARGE_LABEL;
  protected readonly tooltipDragDrop = DRAG_AND_DROP_LABEL;
  protected readonly tooltipNew = ADD_ITEM_LABEL;
  protected readonly tooltipEdit = EDIT_ITEM_LABEL;
  protected readonly deleteItemLabel = DELETE_ITEM_LABEL;
  protected readonly deleteItemConfirmationLabel =
    DELETE_ITEM_CONFIRMATION_LABEL;
  protected readonly mwstText = PLUS_MWST_LABEL;
  protected readonly sumLabel = SUM_LABEL;
  protected readonly manufacturerLabel = MANUFACTURER_LABEL;
  protected readonly quantityLabel = QUANTITY_LABEL;
  protected readonly articleNumberLabel = ARTICLE_NUMBER_LABEL;
  protected readonly shortTextLabel = SHORT_TEXT_LABEL;
  protected readonly unitLabel = UNIT_LABEL;
  protected readonly netEpLabel = NET_EP_LABEL;
  protected readonly netGpLabel = NET_GP_LABEL;
  protected readonly vatShortLabel = VAT_SHORT_LABEL;

  // ── Price display ──────────────────────────────────────────────────────────
  protected readonly showListPrice = signal<boolean>(this._settings.showPrice);

  protected readonly sumNettoPrice = signal<number>(0);
  protected readonly sumMwStPrice = signal<number>(0);

  protected readonly vatGroups = computed(() => {
    const owner = this._ownerSignal();
    return [owner?.zeroSet, owner?.reducedSet, owner?.fullSet]
      .filter((s) => s && (s.vatTotal ?? 0) > 0)
      .map((s) => ({ vat: s!.vat ?? 0, vatAmount: s!.vatTotal! }));
  });

  protected readonly discount = signal<number>(0);
  protected readonly surcharge = signal<number>(0);
  protected readonly totalPrice = signal<number>(0);
  protected readonly firstIndexOfManipulationItem = signal<number>(-1);

  // ── VAT select options ─────────────────────────────────────────────────────
  protected readonly vatOptions: HsSelectOption<string>[] = this._settings
    .getVatOptions()
    .map((v) => ({ value: v, label: v }));

  private readonly _defaultVat: number = this._settings.getDefaultVatRate();

  // ── Colspan helpers ────────────────────────────────────────────────────────
  /** Number of content columns (excluding drag handle and actions). */
  protected readonly contentColspan = computed(() => {
    let cols = 5; // articleNumber, infoDesc, manufacturerLabel, amount, unit
    if (this.showListPrice()) cols += 3; // netEP, vat%, netGP
    return cols;
  });

  /** Total column count of the table (used for divider rows). */
  protected readonly tableColspan = computed(() => {
    let cols = this.contentColspan();
    if (this.dragAndDrop()) cols += 1;
    if (this.canEdit()) cols += 1;
    return cols;
  });

  // ── Inputs ─────────────────────────────────────────────────────────────────
  @Input({ required: true })
  public set owner(value: Task) {
    this._owner = value;
    this._ownerSignal.set(value ?? null);

    if (value) {
      this._items.set(value.items ? value.items : []);
    }

    this.arrangeItemsInCart();
    this.calculateCart();
  }
  public readonly isEditable = input<boolean>(true);
  public readonly canEdit = input<boolean>(true);
  public readonly showAddButton = input<boolean>(true);
  public readonly showEditButton = input<boolean>(true);
  public readonly showDeleteButton = input<boolean>(true);

  public canDragAndDrop(item: ArticleItem): boolean {
    return this.dragAndDrop() && this.isDragAndDropPosition(item);
  }

  public drop(event: CdkDragDrop<ArticleItem[]>) {
    const items = [...this._items()];
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this._items.set(items);

    this.updateSortinCart();
  }

  public async updateSortinCart() {
    const nonManipulationItems: ArticleItem[] = this._items().filter((i) =>
      this.isDragAndDropPosition(i),
    );

    this.beginSaving();
    try {
      for (let i = 0; i < nonManipulationItems.length; i++) {
        nonManipulationItems[i].sortNum = i;
        await this._itemService.update(nonManipulationItems[i]);
      }
    } finally {
      this.endSaving();
    }
  }

  public async btnDeleteClicked(item: ArticleItem) {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<boolean>(KfmYesNoDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        title: this.deleteItemLabel,
        text: this.deleteItemConfirmationLabel,
      },
    });

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        this.beginSaving();
        try {
          const ok = await this._itemService.delete(item.uId);

          if (ok) {
            // reload items
            const index: number = this._items().findIndex(
              (i) => i.uId === item.uId,
            );

            if (index > -1) {
              const items = [...this._items()];
              items.splice(index, 1);
              this._items.set(items);

              // Get all items with a higher sortnum than the deleted one and reduce 1
              const editableItems: ArticleItem[] = this._items().filter(
                (i) => i.sortNum > item.sortNum,
              );

              // update sortnums
              for (const i of editableItems) {
                i.sortNum -= 1;
                await this._itemService.update(i);
              }
            }

            await this.handleItemDeleted(item);

            this.arrangeItemsInCart();
            await this.refreshTaskVatSums();
            this.calculateCart();
          }
        } finally {
          this.endSaving();
        }
      }

      this.isDialogOpen = false;
    });
  }

  /** Edit item button click event */
  public async btnEditClicked(item: ArticleItem) {
    // Create a copy of the original item
    const copy = { ...item };

    let dialogRef;

    if (item.type === KfmAppItemType.Text) {
      if (this.isDialogOpen) {
        return;
      }

      this.isDialogOpen = true;

      dialogRef = this._dialog.open<ArticleItem>(
        KfmTextArticleDialogComponent,
        {
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          data: { article: copy },
        },
      );
    } else if (
      item.type === KfmAppItemType.Discount ||
      item.type === KfmAppItemType.Surcharge
    ) {
      dialogRef = this._dialog.open<ArticleItem>(
        KfmAppNewDiscountSurchargeItemDialogComponent,
        {
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          data: {
            item: copy,
            cart: this._items(),
            showPrice: this._settings.showPrice,
          },
        },
      );
    } else {
      dialogRef = this._dialog.open<ArticleItem>(KfmArticleDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          item: copy,
          showAmount: true,
          showPrice: this._settings.showPrice,
        },
      });
    }

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        result.taskId = this._owner ? this._owner.uId : '';
        result.type = copy.type;

        await this.onItemAdded(result);
      }

      this.isDialogOpen = false;
    });
  }

  /** Add new item button clicked */
  protected async onNewArticleClicked() {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<ArticleItem>(
      KfmArticleDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          showAmount: true,
          showPrice: this._settings.showPrice,
        },
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        // Create new item
        result.taskId = this._owner ? this._owner.uId : '';
        result.type = KfmAppItemType.Item;

        await this.onItemAdded(result);
      }

      this.isDialogOpen = false;
    });
  }

  /** Option create new text article clicked */
  protected async onNewTextArticleClicked() {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<ArticleItem>(
      KfmTextArticleDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        // Create new item
        result.taskId = this._owner ? this._owner.uId : '';
        result.type = KfmAppItemType.Text;

        await this.onItemAdded(result);
      }

      this.isDialogOpen = false;
    });
  }

  /** Option create new surcharge or discount clicked */
  protected onNewSurchargeDiscountClicked() {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<ArticleItem>(
      KfmAppNewDiscountSurchargeItemDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          item: undefined,
          cart: this._items(),
          showPrice: this._settings.showPrice,
        },
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        // Create new item
        result.taskId = this._owner ? this._owner.uId : '';

        await this.onItemAdded(result);
      }

      this.isDialogOpen = false;
    });
  }

  /** Option select item from catalog clicked */
  protected async onArticleCatalogClicked() {
    if (this.isDialogOpen) {
      return;
    }

    this.isDialogOpen = true;

    const dialogRef = this._dialog.open<ArticleItem[]>(
      KfmArticleCatalogDialogComponent,
      {
        width: '600px',
        height: '100%',
        maxHeight: '90vh',
        maxWidth: '90vw',
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        for (const item of result) {
          // Create new item
          item.taskId = this._owner ? this._owner.uId : '';
          item.type = KfmAppItemType.Item;

          await this.onItemAdded(item);
        }
      }

      this.isDialogOpen = false;
    });
  }

  public btnDragAndDropClicked() {
    this.dragAndDrop.set(!this.dragAndDrop());
  }

  // ── Inline edit – blur handlers ────────────────────────────────────────────

  protected onArticleNumberBlur(item: ArticleItem, event: FocusEvent): void {
    const value = (event.target as HTMLInputElement).value;
    if (value === (item.articleNumber ?? '')) return;
    const copy = { ...item };
    copy.articleNumber = value.trim();
    this.saveInlineItem(copy);
  }

  protected onInfoDescBlur(item: ArticleItem, event: FocusEvent): void {
    const value = (event.target as HTMLInputElement).value;
    if (value === (item.infoDesc ?? '')) return;
    const copy = { ...item };
    copy.infoDesc = value.trim();
    this.saveInlineItem(copy);
  }

  protected onManufacturerBlur(item: ArticleItem, event: FocusEvent): void {
    const value = (event.target as HTMLInputElement).value;
    if (value === (item.manufacturerLabel ?? '')) return;
    const copy = { ...item };
    copy.manufacturerLabel = value.trim();
    this.saveInlineItem(copy);
  }

  protected onAmountBlur(item: ArticleItem, event: FocusEvent): void {
    const valueStr = (event.target as HTMLInputElement).value;
    const newAmount = this.parseNumber(valueStr);
    if (newAmount === item.amount) return;
    const copy = { ...item };
    copy.amount = newAmount;
    this.saveInlineItem(copy);
  }

  protected onUnitBlur(item: ArticleItem, event: FocusEvent): void {
    const value = (event.target as HTMLInputElement).value;
    if (value === (item.unit ?? '')) return;
    const copy = { ...item };
    copy.unit = value.trim();
    this.saveInlineItem(copy);
  }

  protected onNetPriceBlur(item: ArticleItem, event: FocusEvent): void {
    const valueStr = (event.target as HTMLInputElement).value;
    const newNet = this.parseNumber(valueStr);
    if (newNet === (item.priceTotal?.netTotal ?? 0)) return;
    const copy = { ...item, priceTotal: { ...(item.priceTotal ?? {}) } };
    copy.priceTotal.netTotal = newNet;
    this.recalculatePriceFromNet(copy);
    this.saveInlineItem(copy);
  }

  protected onVatChanged(
    item: ArticleItem,
    event: HsSelectOption<string> | string,
  ): void {
    const vatStr =
      typeof event === 'string'
        ? event
        : ((event as HsSelectOption<string>)?.value ?? `${this._defaultVat}%`);
    const newVat = this.parsePercent(vatStr);
    if (newVat === (item.priceTotal?.vat ?? this._defaultVat)) return;
    const copy = { ...item, priceTotal: { ...(item.priceTotal ?? {}) } };
    copy.priceTotal.vat = newVat;
    this.recalculatePriceFromNet(copy);
    this.saveInlineItem(copy);
  }

  // ── Inline edit – helpers ──────────────────────────────────────────────────

  protected getVatString(item: ArticleItem): string {
    return `${item.priceTotal?.vat ?? this._defaultVat}%`;
  }

  protected getLineTotal(item: ArticleItem): number {
    return this.roundTo2((item.amount ?? 0) * (item.priceTotal?.netTotal ?? 0));
  }

  protected isTextItem(item: ArticleItem): boolean {
    return item.type === KfmAppItemType.Text;
  }

  // ── Tooltip helpers ────────────────────────────────────────────────────────
  public showFullName(item: ArticleItem): string {
    let name = this.getItemHeader(item);
    name +=
      ' ' +
      this.getItemSubHeader(item)
        .replace("<span class='font-normal'>", '')
        .replace('</span>', '');

    return name;
  }

  // Check if item is of type text
  public isTextPosition(item: ArticleItem): boolean {
    return !item.type && item.type === KfmAppItemType.Text;
  }

  // Check if item is of type discount or surcharge
  public isManipulationPosition(item: ArticleItem): boolean {
    return (
      item.articleNumber === 'Discount' ||
      item.articleNumber === 'Surcharge' ||
      (item.type &&
        (item.type === KfmAppItemType.Discount ||
          item.type === KfmAppItemType.Surcharge))
    );
  }

  public isDragAndDropPosition(item: ArticleItem): boolean {
    if (this.isManipulationPosition(item)) {
      if (item.applyTo && item.applyTo > 0) return true;
    } else return true;

    return false;
  }

  // Build the string for the header in the list item
  public getItemHeader(item: ArticleItem): string {
    let desc = '';

    if (this.isManipulationPosition(item)) {
      if (item.infoDesc) desc += item.infoDesc ? item.infoDesc : '';
      else {
        desc =
          item.type === KfmAppItemType.Discount
            ? this.discountLabel
            : this.surchargeLabel;

        if (!item.percentage)
          desc += ' (' + (item.priceTotal?.netTotal ?? 0) + '€)';
        else desc += ' (' + item.percentage + '%)';
      }
    } else {
      desc =
        !!item.articleNumber && item.articleNumber.trim().length > 0
          ? '[' + item.articleNumber + '] '
          : '';

      desc += item.infoDesc ? item.infoDesc : '';
    }

    return desc;
  }

  // Build the string for the sub header of the list item
  public getItemSubHeader(item: ArticleItem): string {
    let desc = '';

    if (this.isManipulationPosition(item) && !!item.itemIds) {
      desc = `<span class='font-normal'>${this.itemLabel}</span>: `;

      item.itemIds.forEach((id: string) => {
        const cartItem: ArticleItem | undefined = this._items().find(
          (i) => i.uId === id,
        );

        if (cartItem) {
          desc += this.getItemHeader(cartItem) + '; ';
        }
      });
    } else {
      desc =
        !!item.manufacturerLabel && item.manufacturerLabel.trim().length > 0
          ? `${this.manufacturerLabel}: <span class='font-normal'>` +
            item.manufacturerLabel +
            '</span>'
          : '';

      if (this.showListPrice) {
        const netPrice = item.priceTotal?.netTotal ?? 0;
        desc += netPrice
          ? desc.trim().length > 0
            ? ', ' +
              `${this.priceLabel}: <span class='font-normal'>` +
              netPrice +
              '€</span>'
            : `${this.priceLabel}: <span class='font-normal'>` +
              netPrice +
              '€</span>'
          : '';
      }

      let amount = item.amount ? item.amount.toString() : '';
      amount +=
        !!item.unit && item.unit.trim().length > 0
          ? amount.length > 0
            ? ' ' + item.unit
            : item.unit
          : '';

      desc += item.amount
        ? desc.trim().length > 0
          ? ', ' +
            `${this.quantityLabel}: <span class='font-normal'>` +
            amount +
            '</span>'
          : `${this.quantityLabel}: <span class='font-normal'>` +
            amount +
            '</span>'
        : '';
    }

    return desc;
  }

  // ── Item persistence ───────────────────────────────────────────────────────

  private async saveInlineItem(item: ArticleItem): Promise<void> {
    this.beginSaving();
    try {
      // Fix comma-decimal notation
      if (item.priceTotal?.netTotal !== undefined) {
        const str = String(item.priceTotal.netTotal);
        if (str.includes(',')) {
          item.priceTotal.netTotal = parseFloat(str.replace(',', '.'));
        }
      }
      const amountStr = String(item.amount);
      if (amountStr.includes(',')) {
        item.amount = parseFloat(amountStr.replace(',', '.'));
      }

      const updatedItem = await this._itemService.createOrUpdate(item);
      if (!updatedItem) return;

      const items = [...this._items()];
      const idx = items.findIndex((i) => i.uId === updatedItem.uId);
      if (idx > -1) {
        items[idx] = updatedItem;
        this._items.set(items);
        if (this._owner) {
          this._owner.items = items;
        }
      }

      if (updatedItem.type !== KfmAppItemType.Text) {
        await this.refreshTaskVatSums();
        this.calculateCart();
      }
    } finally {
      this.endSaving();
    }
  }

  private async onItemAdded(result: ArticleItem) {
    this.beginSaving();
    try {
      await this.onItemAddedInternal(result);
    } finally {
      this.endSaving();
    }
  }

  private async onItemAddedInternal(result: ArticleItem) {
    const items = this._items();

    if (!this.isDragAndDropPosition(result)) {
      result.sortNum = -1;
    } else {
      result.sortNum = items
        ? items.filter((i) => this.isDragAndDropPosition(i)).length + 1
        : 1;
    }

    // On some mobile devices the currency directive is not working
    if (result.priceTotal) {
      result.priceTotal.netTotal = result.priceTotal.netTotal
        ?.toString()
        .includes(',')
        ? Number(result.priceTotal.netTotal.toString().replace(',', '.'))
        : result.priceTotal.netTotal;
    }
    result.amount = result.amount.toString().includes(',')
      ? Number(result.amount.toString().replace(',', '.'))
      : result.amount;
    const newItem = await this._itemService.createOrUpdate(result);

    if (!newItem) {
      this.isDialogOpen = false;
      return;
    }

    if (
      !this._owner.items ||
      this._owner.items.map((i) => i.uId).indexOf(newItem.uId) === -1
    ) {
      this._owner.items = [...items, newItem];
    } else {
      const index = this._owner.items.findIndex((i) => i.uId === newItem.uId);
      this._owner.items[index] = newItem;
    }

    this._items.set(this._owner.items);

    if (newItem.type !== KfmAppItemType.Text) {
      await this.handleItemAdded(newItem);
      await this.refreshTaskVatSums();
      this.calculateCart();
    } else {
      this.arrangeItemsInCart();
    }
  }

  // Handle if an item was added to update discount and surcharge items
  private async handleItemAdded(item: ArticleItem): Promise<void> {
    if (item.type === undefined || item.type === KfmAppItemType.Item) {
      const updates: Promise<unknown>[] = [];

      // Lets check if we have discount on whole cart
      const discountItems: ArticleItem[] = this._items().filter(
        (i) => i.type === KfmAppItemType.Discount,
      );

      discountItems.forEach((discount: ArticleItem) => {
        // Just add to cart discount items
        if (discount.applyTo === KfmAppItemApplyToType.Cart) {
          if (discount.itemIds === undefined) {
            discount.itemIds = [item.uId];
            updates.push(this._itemService.update(discount));
          } else if (!discount.itemIds.includes(item.uId)) {
            discount.itemIds.push(item.uId);
            updates.push(this._itemService.update(discount));
          }
        }
      });

      // Lets check if we have surcharge on whole cart
      const surchargeItems: ArticleItem[] = this._items().filter(
        (i) => i.type === KfmAppItemType.Surcharge,
      );

      surchargeItems.forEach((surcharge: ArticleItem) => {
        if (surcharge.applyTo === KfmAppItemApplyToType.Cart) {
          if (surcharge.itemIds === undefined) {
            surcharge.itemIds = [item.uId];
            updates.push(this._itemService.update(surcharge));
          } else if (!surcharge.itemIds.includes(item.uId)) {
            surcharge.itemIds.push(item.uId);
            updates.push(this._itemService.update(surcharge));
          }
        }
      });

      await Promise.all(updates);

      // Lets make sure that we first have the items and then discount and surcharge
      this.arrangeItemsInCart();
    }
  }

  // Handle if an item was deleted to update discount and surcharge items
  private async handleItemDeleted(item: ArticleItem): Promise<void> {
    if (item.type === undefined || item.type === KfmAppItemType.Item) {
      const updates: Promise<unknown>[] = [];

      // Lets check if we have discount on whole cart
      const discountItems: ArticleItem[] = this._items().filter(
        (i) => i.type === KfmAppItemType.Discount,
      );

      discountItems.forEach((discount: ArticleItem) => {
        if (
          discount.itemIds !== undefined &&
          discount.itemIds.includes(item.uId)
        ) {
          const idIndex: number = discount.itemIds.findIndex(
            (i) => i === item.uId,
          );

          if (idIndex > -1) {
            discount.itemIds.splice(idIndex, 1);
            updates.push(this._itemService.update(discount));
          }
        }
      });

      // Lets check if we have surcharge on whole cart
      const surchargeItems: ArticleItem[] = this._items().filter(
        (i) => i.type === KfmAppItemType.Surcharge,
      );

      surchargeItems.forEach((surcharge: ArticleItem) => {
        if (
          surcharge.itemIds !== undefined &&
          surcharge.itemIds.includes(item.uId)
        ) {
          const idIndex: number = surcharge.itemIds.findIndex(
            (i) => i === item.uId,
          );

          if (idIndex > -1) {
            surcharge.itemIds.splice(idIndex, 1);
            updates.push(this._itemService.update(surcharge));
          }
        }
      });

      await Promise.all(updates);
    }
  }

  private calculateCart(): void {
    this.calcSumListPrice();
    this.calculateDiscount();
    this.calculateSurcharge();
    this.calculateMwSt();
    this.calculateTotal();
    this.refreshCartInOwner();
  }

  // The backend recalculates and persists the task VAT sums on every
  // PutItem/DeleteItem call. The in-memory owner is not aware of these new
  // values, so we refetch them and update the owner signal to refresh the
  // VAT block in the sum block.
  private async refreshTaskVatSums(): Promise<void> {
    if (!this._owner?.uId) {
      return;
    }

    const refreshed = await this._taskService.getById(this._owner.uId);

    if (refreshed) {
      this._owner.zeroSet = refreshed.zeroSet;
      this._owner.reducedSet = refreshed.reducedSet;
      this._owner.fullSet = refreshed.fullSet;
      this._ownerSignal.set({ ...this._owner });
    }
  }

  private refreshCartInOwner(): void {
    if (this._owner) {
      this._owner.items = this._items();
    }
  }

  private calcSumListPrice(): void {
    let sum = 0;

    if (!!this._items() && this._items().length > 0) {
      this._items()
        .filter(
          (i) => !this.isManipulationPosition(i) && !this.isTextPosition(i),
        )
        .forEach((item: ArticleItem) => {
          sum += (item.priceTotal?.netTotal ?? 0) * item.amount;
        });
    }

    this.sumNettoPrice.set(Math.round((sum + Number.EPSILON) * 100) / 100);
  }

  private calculateDiscount(): void {
    const discountItems: ArticleItem[] = this._items().filter(
      (i) => i.type === KfmAppItemType.Discount,
    );
    this.discount.set(
      Math.round(
        (this.calculateManipulationItems(discountItems) + Number.EPSILON) * 100,
      ) / 100,
    );
  }

  private calculateSurcharge(): void {
    const surchargeItems: ArticleItem[] = this._items().filter(
      (i) => i.type === KfmAppItemType.Surcharge,
    );
    this.surcharge.set(
      Math.round(
        (this.calculateManipulationItems(surchargeItems) + Number.EPSILON) *
          100,
      ) / 100,
    );
  }

  private calculateMwSt() {
    const owner = this._owner;
    const totalVat =
      (owner?.zeroSet?.vatTotal ?? 0) +
      (owner?.reducedSet?.vatTotal ?? 0) +
      (owner?.fullSet?.vatTotal ?? 0);
    this.sumMwStPrice.set(Math.round((totalVat + Number.EPSILON) * 100) / 100);
  }

  private calculateTotal() {
    this.totalPrice.set(
      Math.round(
        (this.sumNettoPrice() -
          this.discount() +
          this.surcharge() +
          this.sumMwStPrice() +
          Number.EPSILON) *
          100,
      ) / 100,
    );
  }

  private calculateManipulationItems(items: ArticleItem[]): number {
    let total = 0;

    // first calculate all unit price items
    let priceItems: ArticleItem[] = items.filter(
      (i) => !i.percentage && (i.priceTotal?.netTotal ?? 0) > 0,
    );

    priceItems.forEach((item: ArticleItem) => {
      total += item.priceTotal?.netTotal ?? 0;
    });

    // then calculate all percentage items
    priceItems = items.filter(
      (i) => i.percentage !== undefined && i.percentage > 0,
    );

    priceItems.forEach((item: ArticleItem) => {
      let sum = 0;

      if (item.itemIds !== undefined) {
        item.itemIds.forEach((id: string) => {
          const cartItem: ArticleItem | undefined = this._items().find(
            (i) => i.uId === id,
          );

          if (cartItem) {
            sum += Number(
              cartItem.amount * (cartItem.priceTotal?.netTotal ?? 0),
            );
          }
        });

        sum = sum * (item.percentage / 100.0);
        sum = Math.round((sum + Number.EPSILON) * 100) / 100;
        total += sum;
      }
    });

    return total;
  }

  // Lets make sure we first list items, text,... ordered by its sortnum
  // and then add discounts and surcharges
  private arrangeItemsInCart(): void {
    // First list all non manipulation items in the cart
    const nonManipulationItems: ArticleItem[] = this._items().filter((i) =>
      this.isDragAndDropPosition(i),
    );

    // sort the items by its sortnum
    nonManipulationItems.sort(this.compare);

    // Get all discount positions
    const discounts: ArticleItem[] = this._items().filter(
      (i) =>
        i.type === KfmAppItemType.Discount &&
        i.applyTo === KfmAppItemApplyToType.Cart,
    );

    // Get all surcharge positions
    const surcharges: ArticleItem[] = this._items().filter(
      (i) =>
        i.type === KfmAppItemType.Surcharge &&
        i.applyTo === KfmAppItemApplyToType.Cart,
    );

    // Build the cart
    let items = new Array<ArticleItem>().concat(nonManipulationItems);
    items = items.concat(discounts);
    items = items.concat(surcharges);

    this._items.set(items);

    const allItems = this._items();
    const firstManipIdx = allItems.findIndex(
      (i) =>
        this.isManipulationPosition(i) &&
        i.applyTo === KfmAppItemApplyToType.Cart,
    );
    this.firstIndexOfManipulationItem.set(firstManipIdx);
  }

  private compare(a: ArticleItem, b: ArticleItem): number {
    const sortNumA = a.sortNum ?? Number.MAX_VALUE;
    const sortNumB = b.sortNum ?? Number.MAX_VALUE;
    return sortNumA - sortNumB;
  }

  // ── Price calculation helpers ──────────────────────────────────────────────

  private recalculatePriceFromNet(item: ArticleItem): void {
    const pt = item.priceTotal ?? {};
    const net = pt.netTotal ?? 0;
    const vat = pt.vat ?? this._defaultVat;
    const vatTotal = this.roundTo2(net * (vat / 100));
    pt.vatTotal = vatTotal;
    pt.grossTotal = this.roundTo2(net + vatTotal);
    item.priceTotal = pt;
  }

  private parseNumber(value: string): number {
    const parsed = parseFloat(value.replace(',', '.'));
    return isNaN(parsed) ? 0 : parsed;
  }

  private parsePercent(value: string): number {
    const parsed = parseFloat(value.replace('%', '').replace(',', '.'));
    return isNaN(parsed) ? 0 : parsed;
  }

  private roundTo2(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}
